import api from './api';
import ENDPOINTS from './endpoints';

/**
 * Fetch patients for the supervisor picker.
 * @param {string} [search] - Optional search terms (AND across name, customer id, email)
 * @returns {Promise<Object>} patients, emails, totalCount
 */
export async function fetchPatients(search = '') {
  const params = {};
  const trimmed = (search || '').trim();
  if (trimmed) {
    params.search = trimmed;
  }

  const { data: envelope } = await api.get(ENDPOINTS.PATIENTS.LIST, { params });
  
  if (!envelope || !envelope.success) {
    throw new Error(envelope?.message || 'Failed to fetch patients');
  }

  const rawPatients = envelope.data?.patients;
  const emails = envelope.data?.emails || [];

  // Prefer the new shape; fall back to legacy emails-only responses.
  const patients = Array.isArray(rawPatients) && rawPatients.length > 0
    ? rawPatients.map((p) => ({
        email: (p.email || '').toLowerCase(),
        fullName: p.full_name || '',
        customerId: p.customer_id || p.customer_number || '',
        label: p.label || p.email || '',
        role: p.role || 'Patient',
      }))
    : emails.map((email) => ({
        email: (email || '').toLowerCase(),
        fullName: '',
        customerId: '',
        label: email,
        role: 'Patient',
      }));
  
  return {
    patients,
    emails: patients.map((p) => p.email),
    totalCount: envelope.data?.total_count || patients.length,
  };
}

/**
 * Link supervisor to a patient
 * @param {string} email - Patient email address
 * @returns {Promise<Object>} Response from backend
 */
export async function linkPatient(email) {
  const { data: envelope } = await api.post(ENDPOINTS.PATIENTS.LINK, { email });
  
  if (!envelope || !envelope.success) {
    throw new Error(envelope?.error || envelope?.message || 'Failed to link patient');
  }
  
  return envelope.data || envelope;
}

/**
 * Sync Airtable data (for Supervisor role)
 * @returns {Promise<Object>} Response from backend with sync results
 */
export async function syncAirtable() {
  const { data: envelope } = await api.post(ENDPOINTS.AIRTABLE.SYNC);
  
  if (!envelope || !envelope.success) {
    throw new Error(envelope?.error || envelope?.message || 'Failed to sync Airtable');
  }
  
  return envelope.data || envelope;
}

/**
 * Get Airtable sync status by job id (for Supervisor role)
 * @returns {Promise<Object>} Response from backend with sync progress/status
 */
export async function syncAirtableStatus(jobId) {
  if (!jobId) {
    throw new Error('Missing jobId for Airtable sync status');
  }

  const { data: envelope } = await api.get(ENDPOINTS.AIRTABLE.SYNC_STATUS(jobId));

  if (!envelope || !envelope.success) {
    throw new Error(envelope?.error || envelope?.message || 'Failed to fetch Airtable sync status');
  }

  return envelope.data || envelope;
}

